import Form from "@common/components/ui/form/Form";
import { Anchor, Button, Text } from "@mantine/core";
import { purchasesStore } from "@stores/purchases";
import { ReactElement, useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { IProTrialFormProps } from "./types";
import { showSuccessNotification } from "@utils/notifications";
import { getInstancePathname } from "@stores/instances/utils";
import ErrorMessage from "@common/components/text/ErrorMessage";
import { persistUTMQueryParamsToString } from "@common/utils/router";
import { useRouter } from "next/router";
import { IDataFetcherResponseError } from "@common/utils/use-query/types";
import { UpsellPill } from "@components/ui/upsells";
import { IPurchase } from "@stores/purchases/types";

export const useTryProPlan = ({ purchase }: { purchase: IPurchase }) => {
  const { id, hosted_instance } = purchase;

  const [isLoading, setIsLoading] = useState(false);
  const [hasStartedTrial, setHasStartedTrial] = useState(false);
  const [error, setError] = useState<IDataFetcherResponseError>();

  const { mutate: tryProPlan } = purchasesStore.useTryProPlan(id);

  const onSubmit = useCallback(() => {
    setIsLoading(true);

    tryProPlan(null, {
      onError(error) {
        setIsLoading(false);
        setError(error);
      },

      async onSuccess() {
        setHasStartedTrial(true);
        if (hosted_instance) {
          showSuccessNotification({
            title: "Free Pro trial started",
            message: `${getInstancePathname(
              hosted_instance,
            )}’s free 14-day Pro trial has begun.`,
          });
        }

        setIsLoading(false);
      },
    });
  }, [hosted_instance, tryProPlan]);

  return {
    isLoading,
    hasStartedTrial,
    error,
    onSubmit,
  };
};

export default function ProTrialForm({
  purchase,
  campaign,
}: IProTrialFormProps): ReactElement {
  const router = useRouter();

  const { isLoading, error, onSubmit } = useTryProPlan({ purchase });
  const { handleSubmit } = useForm({
    shouldFocusError: true,
    mode: "onChange",
    reValidateMode: "onChange",
  });

  return (
    <Form onSubmit={handleSubmit(onSubmit)} data-testid="pro-trial-form">
      {campaign ? (
        <UpsellPill campaign={campaign} isSubmit disabled={isLoading} />
      ) : (
        <>
          <Text fz="sm" m={0}>
            Try out{" "}
            <Anchor
              href={persistUTMQueryParamsToString(
                router,
                "https://www.metabase.com/product/pro",
              )}
              target="_blank"
              rel="noreferrer"
            >
              all the features of Metabase Pro
            </Anchor>{" "}
            with a free 14-day trial.
          </Text>
          <Text fz="sm" m={0} mt="xs">
            If you want to stick with it, you’ll have the chance to upgrade
            permanently—otherwise your plan will automatically switch back to
            Starter.
          </Text>

          <Button
            type="submit"
            mt="sm"
            loading={isLoading}
            loaderPosition="right"
            disabled={isLoading}
          >
            Start free Pro Trial
          </Button>
        </>
      )}

      <ErrorMessage error={error} />
    </Form>
  );
}
